/**
 * GlobalBannerCenter — THE one global top strip (owner directive 2026-08-14).
 *
 * Before this, three independent strips (GlobalLowBalanceBanner,
 * TrialStatusBanner, UsageLimitBanner) mounted adjacently in App.tsx and
 * STACKED when several fired at once. The directive: ONE nicely-placed band
 * in the platform's cream/gold/ink palette; when more than one notice is
 * eligible it ROTATES between them; when none is, no banner renders at all.
 *
 * Architecture: a tiny registration bus. The center wraps the three source
 * components as children so their eligibility/suppression hooks keep running
 * exactly as before — but instead of rendering their own strip, each calls
 * useRegisterBanner(descriptor) and returns null. The center renders the
 * single band:
 *   - 0 registered  → null (no band, no reserved space).
 *   - 1 registered  → the band, no dots.
 *   - 2+ registered → rotate every 8s (paused while hovered) with one dot
 *     per banner (active dot brass-filled; clicking a dot jumps). Critical
 *     tones sort first and rotation restarts on the most severe whenever the
 *     set of banners changes.
 *
 * The X dismisses only the CURRENT banner — it calls that banner's
 * onDismiss; the source then unregisters per its own dismissal policy
 * (session, daily-5AM, per-severity …) and rotation continues with the rest.
 *
 * Placement contract: same as the strips it replaces — full-width band in
 * normal flow directly below the Header; it pushes page content down and
 * never overlays the header (owner report 2026-07-31).
 *
 * Sources rendered OUTSIDE the provider (standalone/tests) see no context
 * and fall back to rendering their own legacy strip.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { BANNER_BAND_PINNED_Z_INDEX } from '../../styles/layers';
import { mediaMax, NARROW_VIEWPORT_QUERY } from '../../styles/breakpoints';
import { useChatPanelFullScreen } from '../wallet/chatPanelStore';
import { designShows } from '../../design/previewPath';
import { PavilionBannerBandDoor } from '../../design/pavilion/withPavilion';
import { BAND_WORDS, type BannerNotice } from './bannerNotice';

export type BannerTone = 'info' | 'warn' | 'critical';

/**
 * The banner band in the new design (PV5-8:D part 2): in the demonstration tree, and on prest0.ai once
 * config/site-design.json lists `band` as live. Fixed for the life of the page, as the app header's is.
 */
export const SHOWS_NEW_BAND = designShows('band');

export interface BannerDescriptor {
  /** Stable identity — re-registering the same id replaces in place. */
  id: string;
  tone: BannerTone;
  /**
   * What the notice says, as data (the banner band, PV5-8:D part 1, 2026-10-14; ./bannerNotice.ts): the words, the
   * phone's words and the one action. The new design's band draws this, because it may not draw today's styled
   * `content`; today's band draws `content`, which each source builds from this same notice, so the two designs say the
   * same thing.
   */
  notice: BannerNotice;
  content: React.ReactNode;
  /**
   * The phone version of `content` (2026-09-13).
   *
   * Owner report: "banner messages in mobile view need to be more concise. So
   * go through the possible banner messages so they don't take up a lot of
   * space." Measured on a 390px phone, the trial banner's exhausted state was
   * ~109px — three wrapped lines plus a CTA that wrapped to a fourth row — on a
   * screen where the chat transcript was already getting less than half.
   *
   * WHY A FIELD AND NOT JUST A CLAMP. A CSS line-clamp truncates the END of a
   * sentence, and the end is where the actionable half lives ("…contact your
   * prest0 point of contact to activate"). It can make a sentence shorter; it
   * cannot make a shorter sentence that is still true. Only the source knows
   * which clause is load-bearing, so each source writes its own — and the
   * clamp below stays as the backstop for a source that forgets, or an
   * interpolated number that runs long.
   *
   * Absent ⇒ `content` is used at every width, exactly as before.
   */
  shortContent?: React.ReactNode;
  /** Whether the center shows the X for this banner. */
  dismissible: boolean;
  /** Called when the X is pressed while this banner is current. */
  onDismiss?: () => void;
}

export interface BannerBus {
  register: (banner: BannerDescriptor) => void;
  unregister: (id: string) => void;
}

/**
 * What the band shows now, worked out by the center for whichever design draws it (the banner band, PV5-8:D part 1):
 * today's strip below, or the new design's band (src/design/pavilion/band/), which may not decide any of this
 * differently. The center owns the state (who registered, the rotation, the pinning, the phone tier, the tap that opens
 * the phone's words); a band only draws it.
 */
export interface BannerBandView {
  /** The notice on show. */
  readonly current: BannerDescriptor;
  /** Every notice registered, most severe first: the band shows one and rotates through them. */
  readonly notices: readonly BannerDescriptor[];
  /** Which of them is on show. */
  readonly index: number;
  /** The fullscreen chat is up, so the band pins itself to the top of the window, above it. */
  readonly pinned: boolean;
  /** The window is in the phone tier, where a notice's phone words apply. */
  readonly narrow: boolean;
  /** The person tapped the phone's words open: the whole notice shows. */
  readonly expanded: boolean;
  /** Show the notice at this place in `notices` (a dot's press). */
  readonly show: (index: number) => void;
  /** Open the whole notice (the tap on the phone's words). */
  readonly expand: () => void;
  /** The rotation waits while a pointer is over the band. */
  readonly hover: (over: boolean) => void;
  /** The band's element: while pinned its height is measured, and the fullscreen chat insets below it. */
  readonly stripRef: (el: HTMLDivElement | null) => void;
}

/**
 * Null outside a GlobalBannerCenter — source components use that to fall
 * back to rendering their own legacy strip when mounted standalone.
 */
export const BannerBusContext = createContext<BannerBus | null>(null);

/** Rotation cadence between banners when several are registered. */
export const BANNER_ROTATE_INTERVAL_MS = 8000;

const TONE_RANK: Record<BannerTone, number> = { critical: 0, warn: 1, info: 2 };

/* Platform palette — the wallet's cream/gold/ink language (NOT the old blue). */
const TONE_STYLES: Record<BannerTone, { bg: string; fg: string; border: string }> = {
  info: { bg: '#FBF7EC', fg: '#6B5320', border: 'rgba(176,140,60,0.28)' },
  warn: { bg: '#FDF3D7', fg: '#7A5714', border: 'rgba(176,140,60,0.45)' },
  critical: { bg: '#FDECE8', fg: '#8C2F1F', border: 'rgba(159,59,45,0.35)' },
};

/**
 * Layer for the PINNED band (fullscreen-chat only) — defined with the rest of
 * the ladder in src/styles/layers.ts and re-exported here so existing import
 * sites keep working.
 */
export { BANNER_BAND_PINNED_Z_INDEX } from '../../styles/layers';

/**
 * CSS custom property carrying the band's measured height. Published on
 * <html> while the band is PINNED so a full-viewport overlay can inset
 * itself below the band instead of covering it. Absent/0 in normal flow,
 * where the band already occupies its own space and pushes content down.
 */
export const BANNER_BAND_HEIGHT_VAR = '--prest0n-banner-band-height';

/**
 * CSS custom property carrying the height the HOME SCREEN's floating nav
 * occupies at the top of the viewport (its top inset + its own height).
 *
 * WHY (owner report 2026-09-17: "when it's on the home screen, it really
 * needs to be a banner underneath the button … it should be the same width,
 * but not be blocked back behind the buttons"). `/` is the one surface with
 * no in-flow <Header/>: its nav is LandingPage's HomeHeader, a position:fixed
 * pill row at the top right — floating over exactly the strip this band
 * renders into. The band carries no z-index (deliberately: it is page chrome,
 * and raising it would bury the nav's own dropdowns, which hang down THROUGH
 * this strip), so the pills painted over the band's right end — the rotation
 * dots, the dismiss ✕ and, on a narrower window, the action itself.
 *
 * Published by LandingPage while it is mounted and withdrawn on unmount, so
 * it is ABSENT on every other surface and the fallback below is 0px — those
 * surfaces are byte-identical to before. Measured, never assumed: the pill
 * row's inset and height are both clamp()ed against the viewport.
 */
export const FLOATING_NAV_INSET_VAR = '--prest0n-floating-nav-inset';

/**
 * Is the viewport narrow enough that the phone copy applies?
 *
 * Reads the SAME query string the stylesheet interpolates, so the JS choice of
 * WHICH words to render and the CSS choice of how to lay them out can never
 * disagree about where the tier starts. Fails to `false` — the full copy — so a
 * browser without matchMedia shows more rather than less.
 */
function useNarrowViewport(): boolean {
  const [narrow, setNarrow] = useState<boolean>(() => {
    try {
      return window.matchMedia(NARROW_VIEWPORT_QUERY).matches;
    } catch {
      return false;
    }
  });
  useEffect(() => {
    let mq: MediaQueryList;
    try {
      mq = window.matchMedia(NARROW_VIEWPORT_QUERY);
    } catch {
      return undefined;
    }
    const onChange = () => setNarrow(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return narrow;
}

const Strip = styled.div<{ $tone: BannerTone; $pinned: boolean }>`
  position: ${(p) => (p.$pinned ? 'fixed' : 'relative')};
  ${(p) =>
    p.$pinned
      ? `top: 0; left: 0; right: 0; z-index: ${BANNER_BAND_PINNED_Z_INDEX};`
      : /* In normal flow the band starts below whatever floating nav the
           surface put over the top of the page — today only the home
           screen's, which publishes FLOATING_NAV_INSET_VAR while it is mounted.
           Unset (every other surface) resolves to 0px: the band sits at the
           top of the document exactly as it always has. Margin, not padding:
           the band keeps the same slim height the phone tier below was tuned
           for, and the gap above it is the page's own background.

           PINNED mode never takes the offset — the fullscreen takeover covers
           the home screen's nav entirely, so the band belongs at the very top
           of the page there (owner directive 2026-08-31). */
        `margin-top: var(${FLOATING_NAV_INSET_VAR}, 0px);`}
  width: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap; /* single row on desktop; wraps gracefully on mobile */
  gap: 12px;
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${(p) => TONE_STYLES[p.$tone].fg};
  background: ${(p) => TONE_STYLES[p.$tone].bg};
  border-bottom: 1px solid ${(p) => TONE_STYLES[p.$tone].border};

  /* The phone tier (2026-09-13). This band had NO media query of any kind: its
     only concession to a narrow screen was flex-wrap, which is to say it grew
     TALLER. */
  ${mediaMax.tablet} {
    gap: 8px;
    padding: 6px 12px;
    font-size: 0.8rem;
  }
`;

const Content = styled.span<{ $clamped?: boolean }>`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  min-width: 0;

  /* Two lines is the backstop, not the plan: the plan is each source's own
     shortContent. This catches the source that forgot and the interpolated
     number that ran long, and it is two rather than one because a clamp at one
     line turns most of these into an ellipsis and nothing else. */
  ${mediaMax.tablet} {
    ${(p) => (p.$clamped ? `
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-align: center;
    ` : '')}

    /* The action, whatever a source put there, yields to the words — which is
       how the trial banner reached four rows, the fourth being its wrapped
       "activate your account" pill.

       Scoped to what a SOURCE rendered, which is everything inside Content.
       The band's own controls — the rotation dots, the dismiss X — are Content's
       siblings and are untouched by construction. The first version instead
       bounded a, button:not([aria-label]) on the strip, which reached the
       right elements today for the wrong reason: it excluded the band's own
       controls because they happen to be labelled, so a source that labelled
       its CTA properly would have escaped the cap. */
    a, button {
      max-width: 42%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
`;

/**
 * The whole message, made tappable when a shorter one is on screen.
 *
 * A button rather than a click handler on the Strip: the strip already
 * contains the rotation dots and the dismiss X, and nesting those inside a
 * button is invalid and unusable with a keyboard. Inherits everything so it
 * reads as text, not a control — the affordance is the truncation itself.
 */
const ExpandTarget = styled.button`
  appearance: none;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  color: inherit;
  text-align: inherit;
  min-width: 0;
  cursor: pointer;
`;

const Dots = styled.span`
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

/* Active dot is brass-filled; inactive dots keep a brass outline only. */
const Dot = styled.button<{ $active: boolean }>`
  flex: none;
  width: 8px;
  height: 8px;
  padding: 0;
  border-radius: 50%;
  cursor: pointer;
  border: 1px solid #b08c3c;
  background: ${(p) => (p.$active ? '#b08c3c' : 'transparent')};
  transition: background 0.15s ease;
`;

const DismissButton = styled.button`
  flex: none;
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  opacity: 0.7;
  padding: 2px 4px;
  line-height: 0;
  &:hover {
    opacity: 1;
  }
  svg {
    width: 16px;
    height: 16px;
  }
`;

/**
 * Register `banner` with the surrounding GlobalBannerCenter while non-null;
 * replaces by id on change, unregisters on null and on unmount. A no-op when
 * no center is above (the caller should then render its legacy strip —
 * check via useContext(BannerBusContext)).
 */
export function useRegisterBanner(banner: BannerDescriptor | null): void {
  const bus = useContext(BannerBusContext);
  const registeredIdRef = useRef<string | null>(null);

  // Sync after EVERY source render so content stays fresh (balances tick,
  // days count down). This cannot loop: the center's resulting re-render
  // passes identical child elements + a memo-stable context value, so React
  // bails out of re-rendering the sources.
  useEffect(() => {
    if (!bus) return;
    if (banner) {
      if (registeredIdRef.current !== null && registeredIdRef.current !== banner.id) {
        bus.unregister(registeredIdRef.current);
      }
      registeredIdRef.current = banner.id;
      bus.register(banner);
    } else if (registeredIdRef.current !== null) {
      bus.unregister(registeredIdRef.current);
      registeredIdRef.current = null;
    }
  });

  // Unregister on unmount (or if the bus itself is swapped out).
  useEffect(() => {
    return () => {
      if (bus && registeredIdRef.current !== null) {
        bus.unregister(registeredIdRef.current);
        registeredIdRef.current = null;
      }
    };
  }, [bus]);
}

/**
 * Today's band: the strip in the platform's cream, gold and ink, drawn from the center's view. A source's phone
 * copy shows in the phone tier until it is tapped open; a source without one is clamped there instead.
 */
const TodaysBand: React.FC<{ view: BannerBandView }> = ({ view }) => {
  const { current, notices, index, pinned, narrow, expanded } = view;
  const hasShort = Boolean(current.shortContent);
  const showShort = narrow && hasShort && !expanded;
  const count = notices.length;
  return (
    // data-prest0n-chat-safe: a click on this band is NOT a click outside
    // the chat (portalClickGuard, 2026-09-17). While the fullscreen
    // takeover is up the band is the only thing above it, so the pane's
    // outside-click auto-close fired on the band's own controls — the chat
    // closed, throwing the user out of fullscreen, and the band un-pinned
    // back into flow, moving out from under the pointer before mouseup, so
    // the click on "refresh now" never landed. Matched BY VALUE by the
    // guard's selector. (The comment sits OUTSIDE the start tag: a comment
    // inside one is parsed as attributes — the 2026-09-15 black-orb
    // lesson.)
    <Strip
      ref={view.stripRef}
      $tone={current.tone}
      $pinned={pinned}
      role="status"
      aria-live="polite"
      data-testid="global-banner-center"
      data-banner-pinned={pinned ? 'true' : undefined}
      data-prest0n-chat-safe="true"
      onMouseEnter={() => view.hover(true)}
      onMouseLeave={() => view.hover(false)}
    >
      {showShort ? (
        <ExpandTarget
          type="button"
          aria-expanded={false}
          aria-label={BAND_WORDS.expand}
          data-banner-expand="collapsed"
          onClick={view.expand}
        >
          <Content $clamped>{current.shortContent}</Content>
        </ExpandTarget>
      ) : (
        <Content $clamped={narrow && !hasShort}>{current.content}</Content>
      )}
      {count > 1 && (
        <Dots>
          {notices.map((b, i) => (
            <Dot
              key={b.id}
              type="button"
              $active={i === index}
              aria-label={BAND_WORDS.showNotice(i + 1, count)}
              onClick={() => view.show(i)}
            />
          ))}
        </Dots>
      )}
      {current.dismissible && (
        <DismissButton
          type="button"
          aria-label={BAND_WORDS.dismiss}
          onClick={() => current.onDismiss?.()}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </DismissButton>
      )}
    </Strip>
  );
};

export const GlobalBannerCenter: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [banners, setBanners] = useState<BannerDescriptor[]>([]);

  const register = useCallback((banner: BannerDescriptor) => {
    setBanners((prev) => {
      const i = prev.findIndex((b) => b.id === banner.id);
      if (i === -1) return [...prev, banner];
      const next = prev.slice();
      next[i] = banner; // replace in place — registration order is stable
      return next;
    });
  }, []);

  const unregister = useCallback((id: string) => {
    setBanners((prev) => (prev.some((b) => b.id === id) ? prev.filter((b) => b.id !== id) : prev));
  }, []);

  // Memo-stable so a center re-render never invalidates the sources' context.
  const bus = useMemo<BannerBus>(() => ({ register, unregister }), [register, unregister]);

  // Severity-first ordering (critical → warn → info), stable within a tone.
  const sorted = useMemo(
    () => [...banners].sort((a, b) => TONE_RANK[a.tone] - TONE_RANK[b.tone]),
    [banners],
  );
  const count = sorted.length;

  const [index, setIndex] = useState(0);
  const [hovered, setHovered] = useState(false);

  // Whenever the SET of banners (or a tone escalation) changes, restart the
  // rotation on the most severe banner (position 0 of the sorted order).
  const compositionKey = sorted.map((b) => `${b.id}:${b.tone}`).join('|');
  useEffect(() => {
    setIndex(0);
  }, [compositionKey]);

  // Rotate every 8s while more than one banner is registered; pause on hover.
  useEffect(() => {
    if (count < 2 || hovered) return;
    const timer = window.setInterval(() => {
      setIndex((i) => (i + 1) % count);
    }, BANNER_ROTATE_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [count, hovered]);

  const safeIndex = count > 0 ? index % count : 0;
  const current = count > 0 ? sorted[safeIndex] : null;

  // PINNED mode (2026-08-31 consistency directive): the fullscreen chat
  // takeover is a full-viewport position:fixed layer, so an in-flow band is
  // simply invisible behind it. While that takeover is up the band pins
  // itself to the top of the viewport above it — the same one band, the same
  // sources, the same rotation, just a different place to stand. Everywhere
  // else it stays in normal flow, which /desktop depends on: that page sizes
  // its live-desktop frame from this band's DOCUMENT offset, and a band with
  // zero flow height would both grow the frame and cover its top.
  const chatFullScreen = useChatPanelFullScreen();
  const pinned = chatFullScreen && current !== null;

  /**
   * The phone copy, and the tap that reveals the rest (2026-09-13).
   *
   * Expansion is per-banner and resets when the visible banner changes: an
   * expanded trial notice must not hand its expanded state to the update
   * notice that rotates in behind it eight seconds later.
   */
  const narrow = useNarrowViewport();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const expanded = current !== null && expandedId === current.id;
  useEffect(() => {
    setExpandedId((prev) => (prev === current?.id ? prev : null));
  }, [current?.id]);

  // Publish the pinned band's height so the takeover can inset below it.
  // Measured rather than assumed: the copy wraps to two or three lines on
  // narrow viewports, and a hardcoded height would clip it exactly there.
  // The element arrives through a callback ref, so a band that mounts after
  // the pin (the new design's, whose chunk may still be arriving) is
  // measured the moment it is there.
  const [stripEl, setStripEl] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    const root = typeof document !== 'undefined' ? document.documentElement : null;
    if (!root) return;
    if (!pinned) {
      root.style.removeProperty(BANNER_BAND_HEIGHT_VAR);
      return;
    }
    const publish = () => {
      const h = stripEl?.getBoundingClientRect().height ?? 0;
      root.style.setProperty(BANNER_BAND_HEIGHT_VAR, `${Math.round(h)}px`);
    };
    publish();
    let ro: ResizeObserver | null = null;
    try {
      ro = new ResizeObserver(publish);
      if (stripEl) ro.observe(stripEl);
    } catch {
      /* no ResizeObserver — the resize listener below still covers reflows */
    }
    window.addEventListener('resize', publish, { passive: true });
    return () => {
      window.removeEventListener('resize', publish);
      ro?.disconnect();
      root.style.removeProperty(BANNER_BAND_HEIGHT_VAR);
    };
    // Re-measure when the visible banner changes: tones and copy differ in height.
    // narrow and expanded are dependencies because the short and full copy
    // differ in height, and the pinned band publishes that height for the
    // takeover to inset below. Without them, expanding a notice would leave the
    // chat overlapping the two extra lines it just revealed.
  }, [pinned, current?.id, current?.tone, narrow, expanded, stripEl]);

  const show = useCallback((i: number) => setIndex(i), []);
  const expandCurrent = useCallback(() => setExpandedId(current?.id ?? null), [current?.id]);
  const view: BannerBandView | null = current
    ? {
        current,
        notices: sorted,
        index: safeIndex,
        pinned,
        narrow,
        expanded,
        show,
        expand: expandCurrent,
        hover: setHovered,
        stripRef: setStripEl,
      }
    : null;

  // The new design's band is fetched with the first notice and kept from
  // then on, so the last notice can slide away (it is handed null).
  const bandSeen = useRef(false);
  if (view) bandSeen.current = true;

  let band: React.ReactNode = null;
  if (SHOWS_NEW_BAND) {
    band = bandSeen.current ? <PavilionBannerBandDoor view={view} fallback={view ? <TodaysBand view={view} /> : null} /> : null;
  } else if (view) {
    band = <TodaysBand view={view} />;
  }

  return (
    <BannerBusContext.Provider value={bus}>
      {band}
      {children}
    </BannerBusContext.Provider>
  );
};

export default GlobalBannerCenter;
